// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/app/components/layout/pioneer/NotificationsRequired.module.css';
import * as PushSubscriptionService from '@app/features/platform/push/PushSubscriptionService';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {BellRingingIcon, BellSlashIcon} from '@phosphor-icons/react';
import type React from 'react';
import {useCallback, useEffect, useState} from 'react';

// Pioneerchat is a work app: in the installed phone app, notifications must be on.
// Phones only show their own "Allow notifications?" box after a tap, so we explain
// first and open it from a button. Once blocked, the phone won't ask again, so we
// show where to switch it on in Settings.

const BAR_TEXT = msg({message: 'Notifications are off', comment: 'Red bar at the top of the phone app.'});
const TURN_ON = msg({message: 'Turn on', comment: 'Button in the red notifications bar.'});
const ASK_TITLE = msg({message: 'Never miss a team message', comment: 'Title of the turn-on-notifications sheet.'});
const ASK_BODY = msg({
	message: 'Pioneerchat is how the team reaches you. Turn on notifications to get new messages and to-dos on this phone.',
	comment: 'Body of the turn-on-notifications sheet.',
});
const ASK_BUTTON = msg({message: 'Turn on notifications', comment: 'Main button of the turn-on-notifications sheet.'});
const NOT_NOW = msg({message: 'Not now', comment: 'Closes the notifications sheet until the app is opened again.'});
const BLOCKED_TITLE = msg({
	message: 'Notifications are blocked',
	comment: 'Title of the sheet when notifications are blocked.',
});
const BLOCKED_BODY = msg({
	message: "Your phone won't let Pioneerchat ask again. Turn them on in Settings:",
	comment: 'Body of the sheet when notifications are blocked.',
});
const STEP_SETTINGS = msg({message: 'Open Settings', comment: 'Step 1 to allow notifications.'});
const STEP_IOS_NOTIFICATIONS = msg({message: 'Tap Notifications', comment: 'iPhone step 2.'});
const STEP_IOS_APP = msg({message: 'Tap Pioneerchat', comment: 'iPhone step 3.'});
const STEP_IOS_ALLOW = msg({message: 'Turn on Allow Notifications', comment: 'iPhone step 4.'});
const STEP_ANDROID_APPS = msg({message: 'Tap Apps', comment: 'Android step 2.'});
const STEP_ANDROID_APP = msg({message: 'Tap Pioneerchat → Notifications', comment: 'Android step 3.'});
const STEP_ANDROID_ALLOW = msg({message: 'Turn on Allow notifications', comment: 'Android step 4.'});
const RECHECK = msg({message: "I've turned them on", comment: 'Re-checks notification permission.'});
const STILL_OFF = msg({
	message: 'Still off. Check the steps above.',
	comment: 'Shown when the re-check finds notifications still off.',
});

type Permission = NotificationPermission | 'unsupported';

function readPermission(): Permission {
	return typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;
}

function isIos(): boolean {
	const ua = navigator.userAgent;
	return /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
}

/** Notification permission, refreshed whenever the app comes back to the front (e.g. from Settings). */
function usePermission(): [Permission, () => Permission] {
	const [permission, setPermission] = useState<Permission>(readPermission);
	const refresh = useCallback(() => {
		const next = readPermission();
		setPermission(next);
		return next;
	}, []);
	useEffect(() => {
		const onVisible = () => {
			if (document.visibilityState === 'visible') refresh();
		};
		document.addEventListener('visibilitychange', onVisible);
		window.addEventListener('focus', refresh);
		let status: PermissionStatus | null = null;
		void navigator.permissions
			?.query({name: 'notifications' as PermissionName})
			.then((result) => {
				status = result;
				result.onchange = () => refresh();
			})
			.catch(() => {});
		return () => {
			document.removeEventListener('visibilitychange', onVisible);
			window.removeEventListener('focus', refresh);
			if (status) status.onchange = null;
		};
	}, [refresh]);
	return [permission, refresh];
}

// "Not now" lasts until the app is opened again.
let sheetDismissedThisLaunch = false;

/**
 * Wraps the phone app: renders `children(bar)` with the red reminder bar while
 * notifications are off (null once they're on), plus the turn-on sheet.
 */
export function NotificationsRequired({children}: {children: (bar: React.ReactNode) => React.ReactNode}) {
	const {i18n} = useLingui();
	const [permission, refresh] = usePermission();
	const [sheetOpen, setSheetOpen] = useState(() => !sheetDismissedThisLaunch);
	const [stillOff, setStillOff] = useState(false);

	useEffect(() => {
		if (permission === 'granted') void PushSubscriptionService.registerPushSubscription();
	}, [permission]);

	if (permission === 'granted' || permission === 'unsupported') return <>{children(null)}</>;

	const blocked = permission === 'denied';
	const ask = async () => {
		const result = await Notification.requestPermission();
		refresh();
		if (result === 'granted') setSheetOpen(false);
	};
	const recheck = () => {
		const next = refresh();
		setStillOff(next !== 'granted');
		if (next === 'granted') setSheetOpen(false);
	};
	const notNow = () => {
		sheetDismissedThisLaunch = true;
		setSheetOpen(false);
	};

	const steps = isIos()
		? [STEP_SETTINGS, STEP_IOS_NOTIFICATIONS, STEP_IOS_APP, STEP_IOS_ALLOW]
		: [STEP_SETTINGS, STEP_ANDROID_APPS, STEP_ANDROID_APP, STEP_ANDROID_ALLOW];

	const bar = (
		<div className={styles.bar} role="status" data-flx="app.pioneer.notifications-required.bar">
			<BellSlashIcon size={20} aria-hidden="true" data-flx="app.pioneer.notifications-required.bar-icon" />
			<span className={styles.barText} data-flx="app.pioneer.notifications-required.bar-text">
				{i18n._(BAR_TEXT)}
			</span>
			<button
				type="button"
				className={styles.barButton}
				onClick={() => (blocked ? setSheetOpen(true) : void ask())}
				data-flx="app.pioneer.notifications-required.bar-button"
			>
				{i18n._(TURN_ON)}
			</button>
		</div>
	);

	return (
		<>
			{children(bar)}
			{sheetOpen && (
				<div className={styles.backdrop} data-flx="app.pioneer.notifications-required.backdrop">
					<section
						className={styles.sheet}
						role="dialog"
						aria-modal="true"
						aria-labelledby="notifications-required-title"
						data-flx="app.pioneer.notifications-required.sheet"
					>
						<span className={styles.handle} aria-hidden="true" data-flx="app.pioneer.notifications-required.handle" />
						<span
							className={blocked ? styles.iconMuted : styles.icon}
							aria-hidden="true"
							data-flx="app.pioneer.notifications-required.icon"
						>
							{blocked ? (
								<BellSlashIcon size={32} weight="fill" data-flx="app.pioneer.notifications-required.blocked-icon" />
							) : (
								<BellRingingIcon size={32} weight="fill" data-flx="app.pioneer.notifications-required.ask-icon" />
							)}
						</span>
						<h2
							id="notifications-required-title"
							className={styles.title}
							data-flx="app.pioneer.notifications-required.title"
						>
							{i18n._(blocked ? BLOCKED_TITLE : ASK_TITLE)}
						</h2>
						<p className={styles.body} data-flx="app.pioneer.notifications-required.body">
							{i18n._(blocked ? BLOCKED_BODY : ASK_BODY)}
						</p>
						{blocked && (
							<ol className={styles.steps} data-flx="app.pioneer.notifications-required.steps">
								{steps.map((step, index) => (
									<li key={index} className={styles.step} data-flx="app.pioneer.notifications-required.step">
										<span className={styles.stepNumber} data-flx="app.pioneer.notifications-required.step-number">
											{index + 1}
										</span>
										{i18n._(step)}
									</li>
								))}
							</ol>
						)}
						{blocked && stillOff && (
							<p className={styles.stillOff} data-flx="app.pioneer.notifications-required.still-off">
								{i18n._(STILL_OFF)}
							</p>
						)}
						<button
							type="button"
							className={blocked ? styles.secondaryButton : styles.primaryButton}
							onClick={() => (blocked ? recheck() : void ask())}
							data-flx="app.pioneer.notifications-required.main-button"
						>
							{i18n._(blocked ? RECHECK : ASK_BUTTON)}
						</button>
						<button
							type="button"
							className={styles.notNow}
							onClick={notNow}
							data-flx="app.pioneer.notifications-required.not-now"
						>
							{i18n._(NOT_NOW)}
						</button>
					</section>
				</div>
			)}
		</>
	);
}
